import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { SessionService } from '../../core/session.service';
import { LanguageService } from '../../core/services/language.service';
import { ToastService } from '../../core/services/toast.service';
import { formatMoney } from '../../core/utils/offer-price.util';
import { PatientContactRole } from '../../core/services/prevention.service';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';
import { lockPageScroll, unlockPageScroll } from '../../layout/shell/shell.component';
import {
  PreventionOffersService,
  OrganizationOfferDetail,
  OfferSiteAvailability,
  SetSiteAvailabilityRequest,
} from '../../core/services/prevention-offers.service';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';

/** Devises supportées V1 (Lot 9.0-E.E.F section 20) -- aucun référentiel devises n'existe ailleurs dans le repo (audité : `formatMoney` accepte n'importe quel code ISO via `Intl`, mais rien ne liste les devises "supportées"). Liste volontairement minimale (celle explicitement demandée par le lot), jamais un catalogue forex complet. */
export const OFFER_SITE_CURRENCIES = ['EUR', 'MAD'] as const;

/** Une ligne éditable de la page Sites & tarifs -- LEFT JOIN `session.establishments()` + `OfferSiteAvailability` sparse (section 9/10 du lot). */
export interface SiteRow {
  establishmentId: string;
  establishmentName: string;
  active: boolean;
  price: number | null;
  currency: string;
  conditions: string | null;
  /** null = pas d'override sur ce site, retombe sur le défaut groupe (Lot "Modalité de
   *  réalisation (défaut groupe)", 2026-09-12) -- même idiome que patientContactRole ci-dessous. */
  immediateFeasibilityDefault: boolean | null;
  patientContactRole: PatientContactRole | null;
  commercialPreference: boolean;
  /** Lecture seule, calculée par le backend -- jamais comparée dans `rowsEqual` (pas un champ éditable). */
  hasDuplicateCoverage: boolean;
}

function rowsEqual(a: SiteRow, b: SiteRow): boolean {
  return a.active === b.active && a.price === b.price && a.currency === b.currency
    && a.conditions === b.conditions && a.immediateFeasibilityDefault === b.immediateFeasibilityDefault
    && a.patientContactRole === b.patientContactRole && a.commercialPreference === b.commercialPreference;
}

/**
 * Sites & tarifs (Lot 9.0-E.E.F) -- configuration multi-site d'une Offer (`EstablishmentOfferAvailability`).
 * Contrat GET/PUT `/availability` volontairement SPARSE côté backend (seuls les sites actuellement
 * actifs apparaissent) -- cette page reconstruit un état complet éditable via un LEFT JOIN frontend
 * entre `session.establishments()` (liste complète, déjà en mémoire, 0 appel Directory
 * supplémentaire) et cette réponse sparse (section 8/9). État minimal : seules les lignes
 * effectivement modifiées (`edits`) sont stockées ; toute autre ligne se lit depuis `baselineById`
 * (section 10) -- jamais un duplicata des ~50 établissements.
 */
@Component({
  selector: 'app-offer-sites',
  standalone: true,
  imports: [TranslatePipe, RouterLink, PreventionGroupHeaderComponent, ConfirmModalComponent],
  templateUrl: './offer-sites.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offer-sites.component.scss'],
})
export class OfferSitesComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly offersSvc = inject(PreventionOffersService);
  private readonly session = inject(SessionService);
  private readonly language = inject(LanguageService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly currencies = OFFER_SITE_CURRENCIES;

  readonly offerId = toSignal(
    this.route.paramMap.pipe(map(params => params.get('offerId'))),
    { initialValue: null as string | null }
  );

  readonly loadError = signal(false);
  readonly saving = signal(false);

  readonly offerDetail = toSignal(
    toObservable(this.offerId).pipe(
      switchMap(id => id
        ? this.offersSvc.getOffer(id).pipe(catchError(() => { this.loadError.set(true); return of(null); }))
        : of(null)
      )
    ),
    { initialValue: null as OrganizationOfferDetail | null }
  );

  /** Réponse GET initiale -- remplacée par la réponse PUT après un save réussi (section 31, jamais un re-GET). */
  private readonly fetchedAvailability = toSignal(
    toObservable(this.offerId).pipe(
      switchMap(id => id
        ? this.offersSvc.getAvailability(id).pipe(catchError(() => { this.loadError.set(true); return of(null); }))
        : of(null)
      )
    ),
    { initialValue: null as OfferSiteAvailability[] | null }
  );
  private readonly savedAvailability = signal<OfferSiteAvailability[] | null>(null);
  private readonly availability = computed(() => this.savedAvailability() ?? this.fetchedAvailability());

  readonly loading = computed(() => this.offerDetail() === null || this.availability() === null);
  readonly isArchived = computed(() => this.offerDetail()?.active === false);
  readonly totalSiteCount = computed(() => this.session.establishments().length);
  readonly bilanCount = computed(() => this.offerDetail()?.bilans.length ?? 0);

  /** LEFT JOIN (section 9) : établissement absent de `availability` -> ligne par défaut (section 25), jamais une erreur. */
  readonly baselineById = computed<Map<string, SiteRow> | null>(() => {
    const av = this.availability();
    if (av === null) return null;
    const byId = new Map(av.map(a => [a.establishmentId, a]));
    const rows = new Map<string, SiteRow>();
    for (const est of this.session.establishments()) {
      const found = byId.get(est.id);
      rows.set(est.id, found ? {
        establishmentId: est.id,
        establishmentName: est.name,
        active: found.active,
        price: found.price,
        currency: found.currency ?? 'EUR',
        conditions: found.conditions,
        // null = pas d'override, retombe sur le défaut groupe (Lot "Modalité de réalisation
        // (défaut groupe)", 2026-09-12) -- jamais forcé à true ici, propagé tel quel depuis le
        // backend (voir offerDetail().defaultImmediateFeasibility pour le libellé effectif).
        immediateFeasibilityDefault: found.immediateFeasibilityDefault,
        patientContactRole: found.patientContactRole,
        commercialPreference: found.commercialPreference,
        hasDuplicateCoverage: found.hasDuplicateCoverage,
      } : {
        establishmentId: est.id,
        establishmentName: est.name,
        active: false,
        price: null,
        currency: 'EUR',
        conditions: null,
        // Jamais activée sur ce site : hérite du défaut groupe dès activation (Lot "Modalité de
        // réalisation (défaut groupe)", 2026-09-12) -- avant ce lot, codé en dur à true.
        immediateFeasibilityDefault: null,
        patientContactRole: null,
        commercialPreference: false,
        hasDuplicateCoverage: false,
      });
    }
    return rows;
  });

  /** Overrides des lignes réellement modifiées uniquement -- jamais un duplicata des lignes inchangées (section 10). */
  readonly edits = signal<Map<string, SiteRow>>(new Map());

  effectiveRow(establishmentId: string): SiteRow {
    return this.edits().get(establishmentId) ?? this.baselineById()!.get(establishmentId)!;
  }

  isDirty(establishmentId: string): boolean {
    return this.edits().has(establishmentId);
  }

  private updateRow(establishmentId: string, patch: Partial<SiteRow>): void {
    if (this.isArchived()) return;
    const base = this.baselineById()?.get(establishmentId);
    if (!base) return;
    const next: SiteRow = { ...this.effectiveRow(establishmentId), ...patch };
    this.edits.update(map => {
      const copy = new Map(map);
      if (rowsEqual(next, base)) {
        copy.delete(establishmentId);
      } else {
        copy.set(establishmentId, next);
      }
      return copy;
    });
  }

  readonly dirtyCount = computed(() => this.edits().size);

  /** Garde "modifications non enregistrées" (audit QA) -- réutilise `dirtyCount`, déjà exact
   *  (0 dès que `edits` retombe à `new Map()` après un save réussi, jamais mis à jour ailleurs). */
  readonly hasUnsavedChanges = computed(() => this.dirtyCount() > 0);
  readonly showLeaveConfirm = signal(false);
  private pendingLeaveResolve: ((allow: boolean) => void) | null = null;

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;
    this.showLeaveConfirm.set(true);
    lockPageScroll({ inertBackground: false });
    return new Promise<boolean>(resolve => { this.pendingLeaveResolve = resolve; });
  }

  confirmLeaveNavigation(): void {
    this.showLeaveConfirm.set(false);
    unlockPageScroll();
    this.pendingLeaveResolve?.(true);
    this.pendingLeaveResolve = null;
  }

  cancelLeaveNavigation(): void {
    this.showLeaveConfirm.set(false);
    unlockPageScroll();
    this.pendingLeaveResolve?.(false);
    this.pendingLeaveResolve = null;
  }

  /** Compteur local projeté (section 37) -- reflète immédiatement les toggles non encore enregistrés. */
  readonly activeSiteCountProjected = computed(() => {
    const baseline = this.baselineById();
    if (!baseline) return 0;
    return this.session.establishments().filter(est => this.effectiveRow(est.id).active).length;
  });

  // ── Recherche (section 19) ──
  readonly search = signal('');
  onSearchChanged(value: string): void {
    this.search.set(value);
  }

  readonly visibleRows = computed<SiteRow[]>(() => {
    const baseline = this.baselineById();
    if (!baseline) return [];
    const query = this.search().trim().toLowerCase();
    const all = this.session.establishments().map(est => this.effectiveRow(est.id));
    return query ? all.filter(row => row.establishmentName.toLowerCase().includes(query)) : all;
  });

  // ── Sélection (sections 17/18) -- concept indépendant de `active`, jamais confondu. ──
  readonly selectedIds = signal<Set<string>>(new Set());

  isSelected(establishmentId: string): boolean {
    return this.selectedIds().has(establishmentId);
  }

  toggleSelect(establishmentId: string): void {
    this.selectedIds.update(set => {
      const copy = new Set(set);
      if (copy.has(establishmentId)) copy.delete(establishmentId); else copy.add(establishmentId);
      return copy;
    });
  }

  /** Uniquement les sites visibles après filtre (section 18) -- jamais les sites masqués par la recherche. */
  readonly allVisibleSelected = computed(() => {
    const visible = this.visibleRows();
    return visible.length > 0 && visible.every(row => this.selectedIds().has(row.establishmentId));
  });

  toggleSelectAllVisible(): void {
    const visibleIds = this.visibleRows().map(row => row.establishmentId);
    const allSelected = this.allVisibleSelected();
    this.selectedIds.update(set => {
      const copy = new Set(set);
      for (const id of visibleIds) {
        if (allSelected) copy.delete(id); else copy.add(id);
      }
      return copy;
    });
  }

  // ── Prix commun (sections 15/16) -- 100% frontend, jamais d'appel API, jamais d'activation implicite. ──
  readonly commonPriceDraft = signal<number | null>(null);
  readonly commonPriceCurrency = signal<string>('EUR');

  onCommonPriceInput(value: string): void {
    const n = value === '' ? null : Number(value);
    this.commonPriceDraft.set(n === null || isNaN(n) ? null : n);
  }

  onCommonPriceCurrencyChanged(value: string): void {
    this.commonPriceCurrency.set(value);
  }

  readonly canApplyCommonPrice = computed(() =>
    !this.isArchived() && this.selectedIds().size > 0
    && this.commonPriceDraft() !== null && this.commonPriceDraft()! >= 0
  );

  applyCommonPrice(): void {
    if (!this.canApplyCommonPrice()) return;
    const price = this.commonPriceDraft()!;
    const currency = this.commonPriceCurrency();
    // Modifie UNIQUEMENT price/currency, jamais `active` (section 16) -- décision distincte du switch.
    for (const id of this.selectedIds()) {
      this.updateRow(id, { price, currency });
    }
  }

  // ── Actif / inactif (sections 12/13/14) ──
  onToggleActive(establishmentId: string): void {
    const row = this.effectiveRow(establishmentId);
    this.updateRow(establishmentId, { active: !row.active });
  }

  onPriceInput(establishmentId: string, value: string): void {
    const n = value === '' ? null : Number(value);
    this.updateRow(establishmentId, { price: n === null || isNaN(n) ? null : n });
  }

  onCurrencyChanged(establishmentId: string, value: string): void {
    this.updateRow(establishmentId, { currency: value });
  }

  // ── Configuration avancée : ligne extensible, un seul site ouvert à la fois (section 21) ──
  readonly expandedId = signal<string | null>(null);

  isExpanded(establishmentId: string): boolean {
    return this.expandedId() === establishmentId;
  }

  toggleExpand(establishmentId: string): void {
    this.expandedId.update(current => current === establishmentId ? null : establishmentId);
  }

  onConditionsChanged(establishmentId: string, value: string): void {
    this.updateRow(establishmentId, { conditions: value.trim() || null });
  }

  /** '' = pas d'override, retombe sur le défaut groupe (Lot "Modalité de réalisation (défaut
   *  groupe)", 2026-09-12) -- même pattern que {@link onContactRoleChanged} ci-dessous. */
  onFeasibilityChanged(establishmentId: string, value: string): void {
    this.updateRow(establishmentId, { immediateFeasibilityDefault: value === '' ? null : value === 'true' });
  }

  /** Libellé dynamique de l'option "Défaut du groupe" du select tri-état (ex. "Défaut du groupe
   *  (Réalisable aujourd'hui)") -- lu depuis offerDetail() déjà chargé sur cette page. */
  groupFeasibilityOptionLabel(): string {
    const detail = this.offerDetail();
    const value = detail
      ? this.translate.instant(detail.defaultImmediateFeasibility
          ? 'preventionOffres.offerSites.feasibilityOptionImmediate'
          : 'preventionOffres.offerSites.feasibilityOptionLater')
      : '';
    return this.translate.instant('preventionOffres.offerSites.feasibilityGroupDefaultOption', { value });
  }

  onContactRoleChanged(establishmentId: string, value: string): void {
    this.updateRow(establishmentId, { patientContactRole: (value === '' ? null : value) as PatientContactRole | null });
  }

  onToggleCommercialPreference(establishmentId: string): void {
    const row = this.effectiveRow(establishmentId);
    this.updateRow(establishmentId, { commercialPreference: !row.commercialPreference });
  }

  // ── Validation avant PUT (section 29) -- jamais uniquement le 400 backend. ──
  readonly invalidDirtyIds = computed(() => {
    const invalid = new Set<string>();
    for (const id of this.edits().keys()) {
      const row = this.effectiveRow(id);
      if (row.active && (row.price === null || row.price < 0 || !row.currency)) {
        invalid.add(id);
      }
    }
    return invalid;
  });

  isInvalid(establishmentId: string): boolean {
    return this.invalidDirtyIds().has(establishmentId);
  }

  readonly canSave = computed(() =>
    !this.isArchived() && !this.saving() && this.dirtyCount() > 0 && this.invalidDirtyIds().size === 0
  );

  formatPrice(row: SiteRow): string {
    if (!row.active || row.price === null) {
      return this.translate.instant('preventionOffres.offerSites.noPrice');
    }
    return formatMoney(row.price, row.currency, this.language.currentLang());
  }

  save(): void {
    if (!this.canSave()) return;
    const offerId = this.offerId();
    if (!offerId) return;

    const sites: SetSiteAvailabilityRequest[] = [...this.edits().entries()].map(([establishmentId, row]) => ({
      establishmentId,
      active: row.active,
      price: row.price,
      currency: row.currency,
      immediateFeasibilityDefault: row.immediateFeasibilityDefault,
      conditions: row.conditions,
      patientContactRole: row.patientContactRole,
      commercialPreference: row.commercialPreference,
    }));

    this.saving.set(true);
    // takeUntilDestroyed (fix #6, revue technique 2026-09-03) : si le composant est détruit pendant
    // que cette requête est en vol, le callback ne s'exécute jamais (pas de toast/état orphelin après
    // navigation). Contrairement à offer-form-page.component.ts#onSave, `edits` n'est PAS vidé de
    // façon synchrone au clic ici : `edits` porte le DIFF lui-même (pas un simple drapeau "dirty" sur
    // des champs déjà affichés), le vider avant la réponse ferait perdre la saisie pour de bon en cas
    // d'échec réseau -- le commentaire ci-dessous ("le backend reste seul autoritaire") documente déjà
    // ce choix délibéré, conservé tel quel.
    this.offersSvc.setAvailability(offerId, { sites }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.saving.set(false);
        // Le backend reste seul autoritaire (section 31) -- reconstruit la baseline depuis la
        // réponse réelle, jamais depuis le payload envoyé.
        this.savedAvailability.set(response);
        this.edits.set(new Map());
        this.toast.show(
          this.translate.instant('preventionOffres.offerSites.saveSuccess'),
          'success',
          3000,
          this.translate.instant('preventionOffres.offerSites.saveSuccessSubtitle')
        );
      },
      error: (err) => {
        this.saving.set(false);
        // Transaction atomique côté backend (section 30) -- aucune ligne n'est marquée sauvegardée,
        // `edits` reste intact pour permettre à l'utilisateur de corriger puis réessayer.
        const backendMessage = (err as { error?: { message?: string } })?.error?.message;
        if (backendMessage) {
          this.toast.show(backendMessage, 'error');
        } else {
          this.toast.show(
            this.translate.instant('preventionOffres.offerSites.saveError'),
            'error',
            3000,
            this.translate.instant('preventionOffres.offerSites.saveErrorSubtitle')
          );
        }
      },
    });
  }

  goBack(): void {
    const id = this.offerId();
    if (!id) return;
    this.router.navigate(['/prevention-offres', id]);
  }
}
